import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { departmentIdScopeKey, getFirestorePathDepartmentId, normalizeDepartmentId } from '../utils/normalizeIds';
import { safeStrForConsole } from '../utils/safeErrorLogging';
import type { Timestamp } from 'firebase/firestore';

export type MedErrorDrugCategory = 'HAD' | 'COMMON';

export const MED_ERROR_DRUG_CATEGORY_LABEL_TH: Record<MedErrorDrugCategory, string> = {
  HAD: 'HAD (ยา High-alert)',
  COMMON: 'ยาทั่วไป',
};

/** Visible label for the dashboard filter next to ปีงบประมาณ / ไตรมาส / ระดับความรุนแรง. */
export const MED_ERROR_DRUG_CATEGORY_FILTER_LABEL_TH = 'HAD / ยาทั่วไป';

export function resolveMedErrorDrugCategory(value: unknown): MedErrorDrugCategory {
  return value === 'COMMON' ? 'COMMON' : 'HAD';
}

export interface MedErrorIncidentRow {
  id?: string;
  eventDate: Timestamp | { toDate: () => Date };
  eventDateDisplay?: string;
  quarter: string;
  fiscalYear: number;
  departmentId: string;
  wardRaw?: string;
  ipdOpd: string;
  drugCategory: MedErrorDrugCategory;
  drugGroup: string;
  severity: string;
  severityA?: number;
  severityB?: number;
  severityC?: number;
  severityD?: number;
  severityE?: number;
  severityF?: number;
  severityG?: number;
  severityH?: number;
  severityI?: number;
  rootCause: string;
  workExperienceYears?: number;
}

export interface MedErrorIncidentMeta {
  totalRecordCount?: number;
  lastSyncAt?: { toDate: () => Date } | null;
}

export interface MedErrorIncidentFetchOptions {
  departmentIds?: string[];
  fiscalYear?: number;
  quarter?: string;
  severity?: string;
  rootCause?: string;
  ipdOpd?: string;
  drugCategory?: MedErrorDrugCategory;
}

export const sanitizeDepartmentIds = (ids?: readonly unknown[]) =>
  Array.from(
    new Set(
      (ids ?? [])
        .map((id) => (typeof id === 'string' ? id.trim() : safeStrForConsole(id).trim()))
        .filter(Boolean),
    ),
  );

const buildDepartmentIdQueryCandidates = (departmentId: string): string[] => {
  const trimmed = departmentId.trim();
  if (!trimmed) return [];
  const underscoreVariant = trimmed.replace(/-/g, '_');
  const hyphenVariant = trimmed.replace(/_/g, '-');
  const normalizedVariant = normalizeDepartmentId(trimmed);
  const firestorePathVariant = getFirestorePathDepartmentId(trimmed);
  return Array.from(
    new Set(
      [trimmed, underscoreVariant, hyphenVariant, normalizedVariant, firestorePathVariant]
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
};

const toStrField = (v: unknown): string =>
  typeof v === 'string' ? v : v && typeof (v as { id?: string }).id === 'string' ? (v as { id: string }).id : '';

/** Map Firestore / callable JSON to row shape (shared by client queries and team callable). */
export function medErrorIncidentRecordToRow(docId: string, d: Record<string, unknown>): MedErrorIncidentRow {
  return {
    id: docId,
    eventDate: (d.eventDate as Timestamp) ?? (d.eventDate as { toDate: () => Date }),
    eventDateDisplay: typeof d.eventDateDisplay === 'string' ? d.eventDateDisplay : undefined,
    quarter: typeof d.quarter === 'string' ? d.quarter : '',
    fiscalYear: typeof d.fiscalYear === 'number' && Number.isFinite(d.fiscalYear) ? d.fiscalYear : 0,
    departmentId: toStrField(d.departmentId),
    wardRaw: typeof d.wardRaw === 'string' ? d.wardRaw : undefined,
    ipdOpd: typeof d.ipdOpd === 'string' ? d.ipdOpd : '',
    drugCategory: resolveMedErrorDrugCategory(d.drugCategory),
    drugGroup: typeof d.drugGroup === 'string' ? d.drugGroup : '',
    severity: typeof d.severity === 'string' ? d.severity : '',
    severityA: d.severityA as number | undefined,
    severityB: d.severityB as number | undefined,
    severityC: d.severityC as number | undefined,
    severityD: d.severityD as number | undefined,
    severityE: d.severityE as number | undefined,
    severityF: d.severityF as number | undefined,
    severityG: d.severityG as number | undefined,
    severityH: d.severityH as number | undefined,
    severityI: d.severityI as number | undefined,
    rootCause: typeof d.rootCause === 'string' ? d.rootCause : '',
    workExperienceYears: d.workExperienceYears as number | undefined,
  };
}

export async function fetchMedErrorIncidentMeta(): Promise<MedErrorIncidentMeta | null> {
  const metaSnap = await getDoc(doc(db, 'medErrorIncidentMeta', 'summary'));
  if (!metaSnap.exists()) return null;
  return metaSnap.data() as MedErrorIncidentMeta;
}

export async function fetchMedErrorIncidentData(
  options: MedErrorIncidentFetchOptions = {},
): Promise<MedErrorIncidentRow[]> {
  const coll = collection(db, 'medErrorIncidents');
  const scopedDepartmentIds = sanitizeDepartmentIds(options.departmentIds);
  const isWildcard = scopedDepartmentIds.includes('*');
  const rows: MedErrorIncidentRow[] = [];
  const seenDocIds = new Set<string>();
  const scopedIdSet = new Set(
    scopedDepartmentIds
      .filter((id) => id !== '*')
      .map((id) => departmentIdScopeKey(id)),
  );

  const toStr = (v: unknown): string => (typeof v === 'string' ? v : v && typeof (v as { id?: string }).id === 'string' ? (v as { id: string }).id : '');

  const appendRows = (snapshot: Awaited<ReturnType<typeof getDocs>>) => {
    snapshot.docs.forEach((docSnap) => {
      if (seenDocIds.has(docSnap.id)) return;
      const d = docSnap.data();
      const departmentId = toStr(d.departmentId);
      const normalizedDeptId = departmentIdScopeKey(departmentId);
      if (!isWildcard && scopedIdSet.size > 0 && !scopedIdSet.has(normalizedDeptId)) return;
      seenDocIds.add(docSnap.id);
      rows.push(medErrorIncidentRecordToRow(docSnap.id, d as Record<string, unknown>));
    });
  };

  if (!scopedDepartmentIds.length) {
    return [];
  }

  if (isWildcard) {
    const { fetchMedErrorTeamRows } = await import('./medErrorTeamClient');
    const fromServer = await fetchMedErrorTeamRows({});
    fromServer.forEach((row) => {
      if (seenDocIds.has(row.id ?? '')) return;
      const id = row.id ?? '';
      if (!id) return;
      seenDocIds.add(id);
      rows.push(row);
    });
  } else {
    const settled = await Promise.allSettled(
      scopedDepartmentIds.flatMap((departmentId) =>
        buildDepartmentIdQueryCandidates(departmentId).map((candidateId) =>
          getDocs(query(coll, where('departmentId', '==', candidateId))),
        ),
      ),
    );
    let permissionDeniedCount = 0;
    let fulfilledQueryCount = 0;
    settled.forEach((result) => {
      if (result.status === 'fulfilled') {
        fulfilledQueryCount += 1;
        appendRows(result.value);
        return;
      }
      const code = (result.reason as { code?: string } | null)?.code ?? '';
      const msg = (result.reason as { message?: string } | null)?.message?.toLowerCase?.() ?? '';
      if (
        code === 'permission-denied' ||
        code === 'failed-precondition' ||
        msg.includes('permission') ||
        msg.includes('insufficient')
      ) {
        permissionDeniedCount += 1;
        return;
      }
      throw result.reason;
    });
    if (!rows.length && fulfilledQueryCount === 0 && permissionDeniedCount > 0) {
      const diag = JSON.stringify({
        scope: 'medErrorIncident',
        scopedDepartmentIds,
        fulfilledQueryCount,
        permissionDeniedCount,
      });
      console.error(`[medErrorIncidentData] permission-denied diagnostic ${diag}`);
      const err = new Error(`Permission denied while reading med error incident scope (depts=[${scopedDepartmentIds.join(',')}])`);
      (err as { code?: string }).code = 'permission-denied';
      throw err;
    }
  }

  let filtered = rows;
  if (options.fiscalYear != null) {
    filtered = filtered.filter((r) => r.fiscalYear === options.fiscalYear);
  }
  if (options.quarter) {
    filtered = filtered.filter((r) => r.quarter === options.quarter);
  }
  if (options.severity) {
    filtered = filtered.filter((r) => r.severity === options.severity);
  }
  if (options.rootCause) {
    filtered = filtered.filter((r) => r.rootCause === options.rootCause);
  }
  if (options.ipdOpd) {
    filtered = filtered.filter((r) => r.ipdOpd === options.ipdOpd);
  }
  if (options.drugCategory) {
    filtered = filtered.filter((r) => r.drugCategory === options.drugCategory);
  }
  return filtered;
}

/**
 * Parse dashboard labels like "1/2568" (quarter / Buddhist-era fiscal year) for chronological ordering.
 */
export function parseMedErrorQuarterLabel(label: string): { quarter: number; year: number } | null {
  const m = label.trim().match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!m) return null;
  const quarter = parseInt(m[1], 10);
  const year = parseInt(m[2], 10);
  if (!Number.isFinite(quarter) || !Number.isFinite(year)) return null;
  return { quarter, year };
}

/** Chronological sort: year ascending, then quarter within the year (e.g. 1/2568 … 4/2568, then 1/2569 …). */
export function compareMedErrorQuarterLabels(a: string, b: string): number {
  const pa = parseMedErrorQuarterLabel(a);
  const pb = parseMedErrorQuarterLabel(b);
  if (pa && pb) {
    if (pa.year !== pb.year) return pa.year - pb.year;
    return pa.quarter - pb.quarter;
  }
  if (pa && !pb) return -1;
  if (!pa && pb) return 1;
  return a.localeCompare(b);
}

export interface MedErrorIncidentAggregates {
  total: number;
  bySeverity: Record<string, number>;
  byDepartment: Record<string, number>;
  byRootCause: Record<string, number>;
  byQuarter: Record<string, number>;
  byIpdOpd: Record<string, number>;
}

export function computeMedErrorIncidentAggregates(
  rows: MedErrorIncidentRow[],
): MedErrorIncidentAggregates {
  const bySeverity: Record<string, number> = {};
  const byDepartment: Record<string, number> = {};
  const byRootCause: Record<string, number> = {};
  const byQuarter: Record<string, number> = {};
  const byIpdOpd: Record<string, number> = {};

  rows.forEach((r) => {
    const sev = r.severity || '—';
    bySeverity[sev] = (bySeverity[sev] ?? 0) + 1;
    const dept = r.departmentId || '—';
    byDepartment[dept] = (byDepartment[dept] ?? 0) + 1;
    const cause = r.rootCause || '—';
    byRootCause[cause] = (byRootCause[cause] ?? 0) + 1;
    const q = r.quarter || '—';
    byQuarter[q] = (byQuarter[q] ?? 0) + 1;
    const io = r.ipdOpd || '—';
    byIpdOpd[io] = (byIpdOpd[io] ?? 0) + 1;
  });

  return {
    total: rows.length,
    bySeverity,
    byDepartment,
    byRootCause,
    byQuarter,
    byIpdOpd,
  };
}
